const TAB_SELECTOR = '[role="tab"]';
const PANEL_SELECTOR = '[data-tabbed-product-panel]';

class TabbedProductCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  abortController = null;

  connectedCallback() {
    this.abortController?.abort();

    this.tabs = Array.from(this.querySelectorAll(TAB_SELECTOR));
    this.panels = Array.from(this.querySelectorAll(PANEL_SELECTOR));

    if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('keydown', this.handleKeydown, { signal });

    const selectedIndex = Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
    this.activate(selectedIndex, { scrollTab: false });
    this.setAttribute('enhanced', '');
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest(TAB_SELECTOR);
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    if (index !== -1) this.activate(index);
  };

  handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') return;

    const index = this.tabs.indexOf(target);
    if (index === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'ArrowRight':
        nextIndex = (index + 1) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.tabs[nextIndex]?.focus();
    this.activate(nextIndex);
  };

  /**
   * @param {number} index
   * @param {{ scrollTab?: boolean }} [options]
   */
  activate(index, { scrollTab = true } = {}) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });

    if (scrollTab) {
      const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.tabs[index]?.scrollIntoView({ behavior: scrollBehavior, block: 'nearest', inline: 'nearest' });
    }
  }
}

if (!customElements.get('tabbed-product-carousel')) {
  customElements.define('tabbed-product-carousel', TabbedProductCarousel);
}
